import React, { useEffect, useRef } from 'react';
import { Scene, PointLayer, Source, ILayer } from '@antv/l7';
import { GaodeMap } from '@antv/l7-maps';

interface MapClusterProps {
  data?: Array<{ lng: number; lat: number; name?: string; status?: string; [key: string]: any }>;
}

const MapCluster: React.FC<MapClusterProps> = ({ data = [] }) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<Scene | null>(null);
  const pointLayerRef = useRef<ILayer | null>(null);
  const pointLayerTextRef = useRef<ILayer | null>(null);
  const mapIdRef = useRef(`mapcluster-${Date.now()}-${Math.random()}`);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    // 设置容器 ID
    mapContainerRef.current.id = mapIdRef.current;

    const scene = new Scene({
      id: mapIdRef.current,
      map: new GaodeMap({
        center: [120.19382669582967, 30.258134],
        style: 'light',
        zoom: 4,
        token: '3c8ea75ab435da6dbc3194b9b20c53b3',
      }),
    });

    sceneRef.current = scene;

    scene.on('loaded', () => {
      // 转换数据格式
      const pointData = data.map((item) => ({
        lng: item.lng,
        lat: item.lat,
        name: item.name || '',
        status: item.status || '',
      }));

      if (pointData.length === 0) return;

      // 使用 Source 类来处理数据并启用聚合
      const dataSource = new Source(pointData, {
        parser: {
          type: 'json',
          x: 'lng',
          y: 'lat',
        },
        cluster: true,
      });

      // 聚合点图层
      const pointLayer = new PointLayer({
        autoFit: false,
      })
        .source(dataSource)
        .shape('circle')
        .scale('point_count', {
          type: 'quantile',
        })
        .size('point_count', [5, 10, 15, 20, 25])
        .active(true)
        .color('rgb(73,167,86)')
        .style({
          strokeWidth: 1,
          stroke: '#fff',
        });

      // 聚合图标注图层
      const pointLayerText = new PointLayer({
        autoFit: false,
      })
        .source(dataSource)
        .shape('point_count', 'text')
        .size(15)
        .active(true)
        .color('#fff')
        .style({
          strokeWidth: 0,
          stroke: '#fff',
        });

      pointLayerRef.current = pointLayer;
      pointLayerTextRef.current = pointLayerText;
      scene.addLayer(pointLayer);
      scene.addLayer(pointLayerText);
    });

    return () => {
      if (sceneRef.current) {
        sceneRef.current.destroy();
      }
    };
  }, []);

  // 当数据变化时更新图层
  useEffect(() => {
    if (!sceneRef.current) return;

    // 转换数据格式
    const pointData = data.map((item) => ({
      lng: item.lng,
      lat: item.lat,
      name: item.name || '',
      status: item.status || '',
    }));

    // 移除旧图层
    if (pointLayerRef.current) {
      sceneRef.current.removeLayer(pointLayerRef.current);
      pointLayerRef.current = null;
    }
    if (pointLayerTextRef.current) {
      sceneRef.current.removeLayer(pointLayerTextRef.current);
      pointLayerTextRef.current = null;
    }

    if (pointData.length === 0) return;

    // 使用 Source 类来处理数据并启用聚合
    const dataSource = new Source(pointData, {
      parser: {
        type: 'json',
        x: 'lng',
        y: 'lat',
      },
      cluster: true,
    });

    // 聚合点图层
    const pointLayer = new PointLayer({
      autoFit: false,
    })
      .source(dataSource)
      .shape('circle')
      .scale('point_count', {
        type: 'quantile',
      })
      .size('point_count', [5, 10, 15, 20, 25])
      .active(true)
      .color('rgb(73,167,86)')
      .style({
        strokeWidth: 1,
        stroke: '#fff',
      });

    // 聚合图标注图层
    const pointLayerText = new PointLayer({
      autoFit: false,
    })
      .source(dataSource)
      .shape('point_count', 'text')
      .size(15)
      .active(true)
      .color('#fff')
      .style({
        strokeWidth: 0,
        stroke: '#fff',
      });

    pointLayerRef.current = pointLayer;
    pointLayerTextRef.current = pointLayerText;
    sceneRef.current.addLayer(pointLayer);
    sceneRef.current.addLayer(pointLayerText);
  }, [data]);

  return <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />;
};

export default MapCluster;
